import React, { useState, useEffect } from 'react';
import { Bell, Save, RotateCcw, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const AlertsSettings = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadConfig();
  }, [user]);

  const loadConfig = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alerts/config?user_id=${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error loading alert config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alerts/config?user_id=${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setSuccessMessage('Configurações salvas com sucesso!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving alert config:', error);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('Deseja restaurar as configurações padrão?')) {
      setConfig({
        ...config,
        consecutive_loss_enabled: true,
        consecutive_loss_threshold: 5,
        bankroll_drop_enabled: true,
        bankroll_drop_percentage: 20.0,
        bankroll_drop_days: 7,
        high_volume_enabled: true,
        high_volume_percentage: 50.0,
        high_volume_period: 'day',
        loss_streak_enabled: true,
        loss_streak_amount: 500.0,
        loss_streak_hours: 24,
        goal_achieved_enabled: true,
        goal_achieved_amount: 100.0,
        goal_achieved_period: 'day',
        positive_streak_enabled: true,
        positive_streak_threshold: 5,
        roi_positive_enabled: true,
        roi_threshold: 15.0,
        pending_bets_enabled: true,
        pending_bets_threshold: 3,
        house_performance_enabled: true,
        house_performance_threshold: -10.0,
        low_balance_enabled: true,
        low_balance_amount: 500.0,
      });
    }
  };

  const updateConfig = (field, value) => {
    setConfig({ ...config, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!config) return null;

  const AlertSection = ({ title, icon: Icon, iconColor, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className={`p-4 border-b border-gray-200 dark:border-gray-700 ${iconColor}`}>
        <div className="flex items-center gap-3">
          <Icon className="w-6 h-6 text-white" />
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
      </div>
      <div className="p-6 space-y-4">
        {children}
      </div>
    </div>
  );

  const ToggleWithInput = ({ label, description, enabled, enabledField, threshold, thresholdField, unit, min, max, step = 1 }) => (
    <div className="flex items-start justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <label className="text-sm font-medium text-gray-900 dark:text-white">
            {label}
          </label>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
        {enabled && threshold !== null && thresholdField && (
          <div className="mt-3 flex items-center gap-2">
            <input
              type="number"
              value={threshold || ''}
              onChange={(e) => {
                const value = e.target.value === '' ? (min || 0) : parseFloat(e.target.value);
                updateConfig(thresholdField, value);
              }}
              onBlur={(e) => {
                // Garante valor válido ao perder foco
                const val = parseFloat(e.target.value);
                if (!e.target.value || (min && val < min)) {
                  updateConfig(thresholdField, min || 0);
                }
              }}
              min={min}
              max={max}
              step={step}
              className="w-24 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">{unit}</span>
          </div>
        )}
      </div>
      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => updateConfig(enabledField, e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
      </label>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            ⚙️ Configurações de Alertas
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Personalize quando e como você quer ser alertado
          </p>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          <p className="text-green-800 dark:text-green-200">{successMessage}</p>
        </div>
      )}

      {/* Alertas de Risco */}
      <AlertSection title="Alertas de Risco" icon={AlertCircle} iconColor="bg-red-500">
        <ToggleWithInput
          label="⚠️ Prejuízo Consecutivo"
          description="Alerta quando você tiver várias apostas vermelhas seguidas"
          enabled={config.consecutive_loss_enabled}
          enabledField="consecutive_loss_enabled"
          threshold={config.consecutive_loss_threshold}
          thresholdField="consecutive_loss_threshold"
          unit="apostas vermelhas"
          min={2}
          max={20}
        />

        <ToggleWithInput
          label="📉 Queda de Banca"
          description="Alerta quando sua banca cair significativamente"
          enabled={config.bankroll_drop_enabled}
          enabledField="bankroll_drop_enabled"
          threshold={config.bankroll_drop_percentage}
          thresholdField="bankroll_drop_percentage"
          unit="% de queda"
          min={5}
          max={50}
          step={5}
        />

        <ToggleWithInput
          label="💸 Volume Alto de Apostas"
          description="Alerta quando você apostar muito da sua banca"
          enabled={config.high_volume_enabled}
          enabledField="high_volume_enabled"
          threshold={config.high_volume_percentage}
          thresholdField="high_volume_percentage"
          unit="% da banca"
          min={10}
          max={100}
          step={5}
        />

        <ToggleWithInput
          label="🎲 Sequência de Perdas"
          description="Alerta quando perder uma quantia específica em pouco tempo"
          enabled={config.loss_streak_enabled}
          enabledField="loss_streak_enabled"
          threshold={config.loss_streak_amount}
          thresholdField="loss_streak_amount"
          unit="R$ perdidos"
          min={100}
          max={5000}
          step={50}
        />
      </AlertSection>

      {/* Alertas Motivacionais */}
      <AlertSection title="Alertas Motivacionais" icon={CheckCircle} iconColor="bg-green-500">
        <div className="flex items-start justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <label className="text-sm font-medium text-gray-900 dark:text-white">
                🎯 Meta de Lucro
              </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Alerta quando você atingir um valor de lucro no período selecionado
            </p>
            {config.goal_achieved_enabled && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={config.goal_achieved_amount || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      updateConfig('goal_achieved_amount', value);
                    }}
                    onBlur={(e) => {
                      // Garante valor válido ao perder foco
                      if (!e.target.value || parseFloat(e.target.value) < 10) {
                        updateConfig('goal_achieved_amount', 10);
                      }
                    }}
                    min={10}
                    max={10000}
                    step={10}
                    className="w-32 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">R$ de lucro</span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={config.goal_achieved_period}
                    onChange={(e) => updateConfig('goal_achieved_period', e.target.value)}
                    className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="day">Por dia</option>
                    <option value="week">Por semana</option>
                    <option value="month">Por mês</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              checked={config.goal_achieved_enabled}
              onChange={(e) => updateConfig('goal_achieved_enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <ToggleWithInput
          label="💚 Sequência Positiva"
          description="Alerta quando você tiver várias apostas verdes seguidas"
          enabled={config.positive_streak_enabled}
          enabledField="positive_streak_enabled"
          threshold={config.positive_streak_threshold}
          thresholdField="positive_streak_threshold"
          unit="greens consecutivos"
          min={3}
          max={20}
        />

        <ToggleWithInput
          label="📈 ROI Positivo"
          description="Alerta quando seu ROI mensal atingir um percentual"
          enabled={config.roi_positive_enabled}
          enabledField="roi_positive_enabled"
          threshold={config.roi_threshold}
          thresholdField="roi_threshold"
          unit="% de ROI"
          min={5}
          max={50}
          step={5}
        />
      </AlertSection>

      {/* Alertas Informativos */}
      <AlertSection title="Alertas Informativos" icon={Info} iconColor="bg-blue-500">
        <ToggleWithInput
          label="📋 Apostas Pendentes"
          description="Alerta quando você tiver muitas apostas sem resultado"
          enabled={config.pending_bets_enabled}
          enabledField="pending_bets_enabled"
          threshold={config.pending_bets_threshold}
          thresholdField="pending_bets_threshold"
          unit="apostas pendentes"
          min={1}
          max={20}
        />

        <ToggleWithInput
          label="🏠 Performance por Casa"
          description="Alerta quando uma casa estiver com ROI abaixo da média"
          enabled={config.house_performance_enabled}
          enabledField="house_performance_enabled"
          threshold={config.house_performance_threshold}
          thresholdField="house_performance_threshold"
          unit="% abaixo da média"
          min={-50}
          max={-5}
          step={5}
        />

        <ToggleWithInput
          label="💰 Saldo Baixo"
          description="Alerta quando sua banca estiver abaixo de um valor"
          enabled={config.low_balance_enabled}
          enabledField="low_balance_enabled"
          threshold={config.low_balance_amount}
          thresholdField="low_balance_amount"
          unit="R$"
          min={100}
          max={5000}
          step={100}
        />
      </AlertSection>

      {/* Botão de Teste */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
              🧪 Modo de Teste
            </h4>
            <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
              Use este botão para verificar se seus alertas estão funcionando corretamente. 
              O sistema irá gerar notificações baseadas nos seus dados atuais.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={async () => {
                  try {
                    console.log('🧪 Teste: Gerando alertas...');
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alerts/generate?user_id=${user.id}`, {
                      method: 'POST'
                    });
                    const data = await response.json();
                    
                    // Limpar histórico de alertas exibidos para ver novamente
                    localStorage.removeItem('stakemind_displayed_alerts');
                    
                    alert(`✅ ${data.new_alerts_count} alertas gerados!\n\nRecarregue a página para ver as notificações toast.`);
                    
                    // Recarregar página após 1 segundo
                    setTimeout(() => window.location.reload(), 1000);
                  } catch (error) {
                    alert('❌ Erro ao gerar alertas: ' + error.message);
                  }
                }}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <Bell className="w-4 h-4" />
                Testar Alertas Agora
              </button>
              
              <button
                onClick={() => {
                  if (window.confirm('Deseja limpar o histórico de alertas exibidos?\n\nIsso fará com que alertas não lidos apareçam novamente.')) {
                    localStorage.removeItem('stakemind_displayed_alerts');
                    alert('✅ Histórico limpo! Recarregue a página para ver alertas não lidos novamente.');
                  }
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Resetar Histórico
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Botões de Ação */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-semibold transition-colors"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Salvando...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Salvar Configurações
            </>
          )}
        </button>

        <button
          onClick={resetToDefaults}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-semibold transition-colors"
        >
          <RotateCcw className="w-5 h-5" />
          Restaurar Padrões
        </button>
      </div>
    </div>
  );
};

export default AlertsSettings;
